/** Gói liệu trình (treatment packages). Real /api/v1/treatment-packages + /patients/{id}/packages. */
import { apiClient } from "./client";

export interface PackageCatalog {
  id: string;
  name: string;
  serviceId: string | null;
  totalSessions: number;
  priceVnd: number;
  validityDays: number | null;
  active: boolean;
}

export interface PackageInput {
  name: string;
  serviceId?: string | null;
  totalSessions: number;
  priceVnd: number;
  validityDays?: number | null;
}

export interface PatientPackage {
  id: string;
  packageName: string;
  sessionsTotal: number;
  sessionsUsed: number;
  sessionsRemaining: number;
  pricePaidVnd: number;
  expiresAt: string | null;
  status: string;
  purchasedAt: string;
  /**
   * Whether redeeming a session right now would be accepted (#18869).
   *
   * Computed by the server next to the guards `redeem` enforces — ACTIVE, not past `expiresAt`,
   * sessions left. Do NOT re-derive it here: a picker that decides for itself is how you end up
   * offering a package the next click refuses.
   */
  redeemable: boolean;
}

export const treatmentPackagesApi = {
  catalog(activeOnly = false): Promise<PackageCatalog[]> {
    return apiClient
      .get<PackageCatalog[]>("/v1/treatment-packages", { params: { activeOnly } })
      .then((r) => r.data);
  },
  create(b: PackageInput): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>("/v1/treatment-packages", b).then((r) => r.data);
  },
  update(id: string, b: PackageInput): Promise<void> {
    return apiClient.put(`/v1/treatment-packages/${id}`, b).then(() => undefined);
  },
  archive(id: string): Promise<void> {
    return apiClient.post(`/v1/treatment-packages/${id}/archive`).then(() => undefined);
  },
  /** Hard delete (permanent). 409 nếu gói đang được tham chiếu. */
  remove(id: string): Promise<void> {
    return apiClient.delete(`/v1/treatment-packages/${id}/permanent`).then(() => undefined);
  },
  forPatient(patientId: string): Promise<PatientPackage[]> {
    return apiClient
      .get<PatientPackage[]>(`/v1/patients/${patientId}/packages`)
      .then((r) => r.data);
  },
  buy(patientId: string, packageId: string, pricePaidVnd?: number): Promise<{ id: string }> {
    return apiClient
      .post<{ id: string }>(`/v1/patients/${patientId}/packages`, { packageId, pricePaidVnd })
      .then((r) => r.data);
  },
  redeem(patientPackageId: string, note?: string): Promise<void> {
    return apiClient
      .post(`/v1/patient-packages/${patientPackageId}/redeem`, { note })
      .then(() => undefined);
  },
};

export const treatmentPackagesQueryKeys = {
  catalog: () => ["treatment-packages-catalog"] as const,
  ofPatient: (patientId: string) => ["patient-packages", patientId] as const,
};
